import {
  moveDynamicFieldsToCustomFields,
  useRealtimeDbBatchMutation,
} from '@/hooks/realtimeDataHelpers';
import {
  selectUserSegmentInteraction,
  useRealtimeExperienceStore,
} from '@/pages/experiences/store/realtimeExperienceStore';
import { RealtimeDbCreateManyValue } from '@/providers/dataProvider/firestoreDataProvider';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';

export const useRealtimeUserSegmentInteraction = ({
  experienceId,
  userId,
  segmentId,
  segmentInteractionId,
}: any) => {
  /**
   * Build user response filter properties object
   * This object is used to select specific user responses from the realtime store
   */
  const userSegmentInteractionSelectFilterProps = {
    experienceId, // Filter by experience ID
    userId, // Filter by user ID
    segmentId, // Filter by segment ID
    segmentInteractionId, // Filter by specific response identifier
  };

  const existingSegmentInteraction = useRealtimeExperienceStore(
    useShallow((state) =>
      selectUserSegmentInteraction(state, userSegmentInteractionSelectFilterProps)
    )
  );

  const setLocalSegmentInteraction = useRealtimeExperienceStore(
    (state) => state.setLocalSegmentInteraction
  );

  const { batchMutation, loading } = useRealtimeDbBatchMutation();

  const handleSetSegmentInteraction = async (userSegmentInteraction: any, options?: any) => {
    try {
      const { experience_id: experienceId } = userSegmentInteraction;

      const batchActionParams: RealtimeDbCreateManyValue[] = [
        {
          resource: 'user_segment_interactions',
          resourcePath: `experiences/${experienceId}/`,
          uuids: [segmentInteractionId],
          data: userSegmentInteraction,
          ids: [segmentInteractionId],
        },
      ];

      batchMutation({
        values: batchActionParams,
        meta: {
          onSuccess: (batchResult: any) => {
            if (options?.onSuccess) {
              options.onSuccess(batchResult);
            }
          },
          onError: (error: any) => {
            Sentry.captureException(
              new Error(error?.message ?? 'setUserSegmentInteraction failed'),
              {
                extra: {
                  op: 'realtimeUserSegmentInteraction:setUserSegmentInteraction',
                  status: error?.statusCode ?? error?.response?.status,
                },
              }
            );
            if (options?.onError) {
              options.onError(error);
            }
          },
        },
      });
    } catch (error) {
      console.log(
        'setUserSegmentInteraction realtimeUserSegmentInteraction => catch condition ',
        error
      );
      Sentry.captureException(
        new Error((error as any)?.message ?? 'setUserSegmentInteraction threw'),
        {
          extra: {
            op: 'realtimeUserSegmentInteraction:setUserSegmentInteraction:catch',
            status: (error as any)?.statusCode ?? (error as any)?.response?.status,
          },
        }
      );

      if (options?.onError) {
        options.onError(error);
      }
    }
  };

  // Stable setter (safe to capture in long-lived DOM listeners): update the
  // local store immediately in the stored custom_fields shape, then save.
  const setUserSegmentInteraction = useCallback((changedFields: any, options?: any) => {
    const latestSegmentInteraction = selectUserSegmentInteraction(
      useRealtimeExperienceStore.getState(),
      userSegmentInteractionSelectFilterProps
    );
    // Convert the change to the stored shape (feature data inside custom_fields)
    // so the store record matches the DB shape and the selectors can read the
    // fresh value from custom_fields directly.
    const segmentInteractionWithModifiedCustomFields =
      moveDynamicFieldsToCustomFields(changedFields);
    const segmentInteractionData = {
      ...latestSegmentInteraction,
      ...segmentInteractionWithModifiedCustomFields,
      custom_fields: {
        ...latestSegmentInteraction?.custom_fields,
        ...segmentInteractionWithModifiedCustomFields.custom_fields,
      },
    };
    setLocalSegmentInteraction(segmentInteractionId, segmentInteractionData);

    handleSetSegmentInteraction(segmentInteractionData, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loading,
    segmentInteraction: existingSegmentInteraction,
    setUserSegmentInteraction,
  };
};
